import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import { SITE_URL } from '../config/seo';
import api from '../utils/api';
import Pagination from '../components/Pagination';
import { stripHtmlTags } from '../utils/htmlUtils';
import { Search, Calendar, User, Tag, ArrowRight, Newspaper } from 'lucide-react';

const NewsListPage = () => {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [news, setNews] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [contentCategories, setContentCategories] = useState([]);
  const [contentTags, setContentTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localSearch, setLocalSearch] = useState('');
  const page = parseInt(searchParams.get('page')) || 1;
  const searchQuery = searchParams.get('search') || '';

  const isCategory = pathname.includes('/news/category/');
  const isTag = pathname.includes('/news/tag/');
  const filterType = isCategory ? 'category' : isTag ? 'tag' : null;
  const filterSlug = (isCategory || isTag) ? slug : null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = { page, limit: 12 };
        if (filterType === 'category' && filterSlug) params.category = filterSlug;
        if (filterType === 'tag' && filterSlug) params.tag = filterSlug;
        if (searchQuery) params.search = searchQuery;

        const [newsRes, catsRes, tagsRes] = await Promise.all([
          api.get('/news', { params }),
          api.get('/content-categories').catch(() => ({ data: [] })),
          api.get('/content-tags').catch(() => ({ data: [] })),
        ]);

        setNews(newsRes.data.news || []);
        setPagination(newsRes.data.pagination || null);
        setContentCategories(Array.isArray(catsRes.data) ? catsRes.data : []);
        setContentTags(Array.isArray(tagsRes.data) ? tagsRes.data : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filterType, filterSlug, page, searchQuery]);

  const baseUrl = pathname.replace(/\?.*$/, '');
  const breadcrumbName = filterType === 'category'
    ? (contentCategories.find((c) => c.slug === filterSlug)?.name || 'Category')
    : filterType === 'tag'
      ? (contentTags.find((t) => t.slug === filterSlug)?.name || 'Tag')
      : 'News';

  const canonical = `${SITE_URL}${pathname.split('?')[0]}`;
  const title = filterType ? `News - ${breadcrumbName}` : 'News - Latest Updates';
  const description = filterType
    ? `Read our ${breadcrumbName.toLowerCase()} news and articles.`
    : 'Read our latest news and updates about Fatehabad.';

  const handleSearch = () => {
    const p = new URLSearchParams(searchParams);
    if (localSearch.trim()) p.set('search', localSearch.trim());
    else p.delete('search');
    p.delete('page');
    setSearchParams(p);
  };

  return (
    <>
      <SEO title={title} description={description} canonical={canonical} />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">

        {/* Modern Hero Section */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
          <div className="container-custom py-12 md:py-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-4 text-sm font-medium text-primary-600 dark:text-primary-400">
                <span className="flex items-center gap-2 bg-primary-50 dark:bg-primary-900/30 px-3 py-1 rounded-full">
                  <Newspaper className="w-4 h-4" />
                  Digital Fatehabad News
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                {filterType ? `${breadcrumbName}` : 'Latest News & Events'}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                Stay updated with the breaking news, local events, and community stories from Fatehabad.
              </p>
            </div>
          </div>
        </div>

        <div className="container-custom py-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <PageLoader message="Loading news..." />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">

              {/* Main Content */}
              <div className="lg:col-span-3">
                {/* Search Bar Mobile */}
                <div className="mb-6 lg:hidden">
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all outline-none"
                      placeholder="Search news..."
                      value={localSearch}
                      onChange={(e) => setLocalSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Search className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                  </div>
                </div>

                {news.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-6">
                    {news.map((n) => (
                      <article
                        key={n._id}
                        className="group flex flex-col bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700 hover:-translate-y-1"
                      >
                        <Link to={`/news/${n.slug}`} className="block relative overflow-hidden aspect-[16/10]">
                          {n.image ? (
                            <img
                              src={n.image.startsWith('http') ? n.image : `${SITE_URL}${n.image.startsWith('/') ? '' : '/'}${n.image}`}
                              alt={n.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400">
                              <Newspaper className="w-12 h-12 opacity-50" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </Link>

                        <div className="flex-1 p-6 flex flex-col">
                          <div className="flex items-center gap-4 text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">
                            <span className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5" />
                              {n.publishedAt && new Date(n.publishedAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                            {n.contentCategory && (
                              <span className="px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
                                {n.contentCategory.name}
                              </span>
                            )}
                          </div>

                          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                            <Link to={`/news/${n.slug}`}>
                              {n.title}
                            </Link>
                          </h2>

                          <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-3 mb-6 flex-1 leading-relaxed">
                            {stripHtmlTags(n.excerpt || n.content || '').slice(0, 150)}...
                          </p>

                          <div className="pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                              <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                <User className="w-3.5 h-3.5" />
                              </div>
                              {n.author || 'Admin'}
                            </div>
                            <Link
                              to={`/news/${n.slug}`}
                              className="text-sm font-semibold text-primary-600 dark:text-primary-400 flex items-center gap-1 group/btn"
                            >
                              Read More
                              <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                            </Link>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">No news found</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-6">Try adjusting your search or filters to find what you're looking for.</p>
                    <button
                      onClick={() => { setLocalSearch(''); setSearchParams({}); }}
                      className="text-primary-600 font-medium hover:underline"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}

                {pagination && pagination.totalPages > 1 && (
                  <div className="mt-12">
                    <Pagination
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      hasNextPage={pagination.hasNextPage}
                      hasPrevPage={pagination.hasPrevPage}
                      onPageChange={(p) => {
                        const newParams = new URLSearchParams(searchParams);
                        newParams.set('page', p);
                        setSearchParams(newParams);
                        window.scrollTo(0, 0);
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="space-y-8">
                {/* Search Widget */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 hidden lg:block">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-4">Search</h3>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all outline-none text-sm"
                      placeholder="Search news..."
                      value={localSearch}
                      onChange={(e) => setLocalSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  </div>
                </div>

                {/* Categories Widget */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-4">Categories</h3>
                  <div className="space-y-2">
                    <Link
                      to="/news"
                      className={`flex items-center justify-between py-2 px-3 rounded-lg text-sm transition-colors ${!filterType ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                    >
                      <span className="font-medium">All News</span>
                    </Link>
                    {contentCategories.map((c) => (
                      <Link
                        key={c._id}
                        to={`/news/category/${c.slug}`}
                        className={`flex items-center justify-between py-2 px-3 rounded-lg text-sm transition-colors ${filterType === 'category' && filterSlug === c.slug ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                      >
                        <span className="font-medium">{c.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Tags Widget */}
                {contentTags.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Tag className="w-4 h-4" /> Popular Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {contentTags.map((t) => (
                        <Link
                          key={t._id}
                          to={`/news/tag/${t.slug}`}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterType === 'tag' && filterSlug === t.slug ? 'bg-primary-600 text-white shadow-md shadow-primary-500/20' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-primary-900/30'}`}
                        >
                          #{t.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Newsletter Box (Visual Only) */}
                <div className="bg-gradient-to-br from-primary-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg">
                  <h3 className="font-bold text-lg mb-2">Subscribe to our newsletter</h3>
                  <p className="text-primary-100 text-sm mb-4">Get the latest business insights delivered to your inbox.</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="Your email"
                      className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-primary-200 text-sm focus:outline-none focus:bg-white/20"
                    />
                    <button className="bg-white text-primary-600 px-3 py-2 rounded-lg text-sm font-bold hover:bg-primary-50 transition-colors">
                      Join
                    </button>
                  </div>
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NewsListPage;
